import { ErrorSubcode, type TServerErrorResponse } from "@/types";
import { isValidSubcode } from "@/lib/errorUtils";

export interface AgentChatRequest {
  session_id?: string;
  agent_type?: "conversational" | "task";
  message: string;
  context?: Record<string, unknown>;
  /** LangGraph interrupt id to resume directly. 409 if the id is stale. */
  resolves_interrupt_id?: string;
}

export interface AgentChatResponse {
  session_id: string;
  response: string;
}

export type AgentFeedbackRating = "up" | "down";
export type AgentServiceId = string | number;

export interface AgentMessageFeedbackRequest {
  rating: AgentFeedbackRating;
  reason: string | null;
}

export interface UpsertAgentMessageFeedbackParams extends AgentMessageFeedbackRequest {
  messageId: string;
}

export interface AgentMessageFeedbackResponse {
  id: AgentServiceId;
  message_id: AgentServiceId;
  session_id: AgentServiceId;
  rating: AgentFeedbackRating;
  reason: string | null;
  created_at: string;
  updated_at: string;
}

export interface AgentCurrentUserFeedback {
  id?: AgentServiceId;
  message_id?: AgentServiceId;
  session_id?: AgentServiceId;
  rating: AgentFeedbackRating;
  reason: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface AgentAssistantMessageMetadata {
  message_id?: AgentServiceId | null;
  session_id?: AgentServiceId | null;
  current_user_feedback?: AgentCurrentUserFeedback | null;
}

export interface AgentConversationHistoryMessage {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp?: string;
  message_id?: AgentServiceId | null;
  session_id?: AgentServiceId | null;
  feedback?: AgentCurrentUserFeedback | null;
  metadata?: Record<string, unknown>;
}

export interface AgentConversationHistoryResponse {
  session_id: string;
  messages: AgentConversationHistoryMessage[];
  total_messages: number;
}

export type NormalizedAgentCurrentUserFeedback = Omit<
  AgentCurrentUserFeedback,
  "id" | "message_id" | "session_id"
> & {
  id?: string;
  message_id?: string;
  session_id?: string;
};

export type NormalizedAgentAssistantMessageMetadata = Omit<
  AgentAssistantMessageMetadata,
  "message_id" | "session_id" | "current_user_feedback"
> & {
  message_id?: string;
  session_id?: string;
  current_user_feedback?: NormalizedAgentCurrentUserFeedback | null;
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
};

const hasOwn = (value: Record<string, unknown>, key: string): boolean =>
  Object.prototype.hasOwnProperty.call(value, key);

const optionalString = (value: unknown): string | undefined =>
  typeof value === "string" && value.length > 0 ? value : undefined;

export const stringifyAgentServiceId = (
  value: AgentServiceId | null | undefined,
): string | undefined => {
  if (typeof value === "string") {
    return value.length > 0 ? value : undefined;
  }
  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : undefined;
};

const optionalIdString = (value: unknown): string | undefined => {
  if (typeof value === "string" || typeof value === "number") {
    return stringifyAgentServiceId(value);
  }
  return undefined;
};

const firstIdString = (
  records: Record<string, unknown>[],
  keys: string[],
): string | undefined => {
  for (const record of records) {
    for (const key of keys) {
      const value = optionalIdString(record[key]);
      if (value) return value;
    }
  }
  return undefined;
};

const firstOwnValue = (
  records: Record<string, unknown>[],
  keys: string[],
): { found: boolean; value: unknown } => {
  for (const record of records) {
    for (const key of keys) {
      if (hasOwn(record, key)) {
        return { found: true, value: record[key] };
      }
    }
  }
  return { found: false, value: undefined };
};

const toCandidateRecords = (payload: unknown): Record<string, unknown>[] => {
  if (!isRecord(payload)) return [];

  const records: Record<string, unknown>[] = [];
  const addRecord = (record: Record<string, unknown>) => {
    records.push(record);
    const metadata = record.metadata;
    if (isRecord(metadata)) {
      records.push(metadata);
      if (isRecord(metadata.custom)) records.push(metadata.custom);
    }
  };

  addRecord(payload);
  if (isRecord(payload.message)) addRecord(payload.message);
  if (isRecord(payload.assistant_message)) addRecord(payload.assistant_message);
  return records;
};

const normalizeAgentCurrentUserFeedback = (
  value: unknown,
  fallback: Pick<AgentAssistantMessageMetadata, "message_id" | "session_id">,
): NormalizedAgentCurrentUserFeedback | null | undefined => {
  if (value === null) return null;
  if (!isRecord(value)) return undefined;

  const rating = value.rating;
  if (rating !== "up" && rating !== "down") return undefined;

  const messageId =
    optionalIdString(value.message_id) ??
    optionalIdString(value.messageId) ??
    optionalIdString(fallback.message_id);
  const sessionId =
    optionalIdString(value.session_id) ??
    optionalIdString(value.sessionId) ??
    optionalIdString(fallback.session_id);
  const reason = typeof value.reason === "string" ? value.reason : null;
  const id = optionalIdString(value.id);

  return {
    ...(id ? { id } : {}),
    ...(messageId ? { message_id: messageId } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
    rating,
    reason,
    ...(optionalString(value.created_at)
      ? { created_at: optionalString(value.created_at) }
      : {}),
    ...(optionalString(value.createdAt)
      ? { created_at: optionalString(value.createdAt) }
      : {}),
    ...(optionalString(value.updated_at)
      ? { updated_at: optionalString(value.updated_at) }
      : {}),
    ...(optionalString(value.updatedAt)
      ? { updated_at: optionalString(value.updatedAt) }
      : {}),
  };
};

export const normalizeAgentAssistantMessageMetadata = (
  payload: unknown,
  fallback: Partial<AgentAssistantMessageMetadata> = {},
): NormalizedAgentAssistantMessageMetadata | undefined => {
  const records = toCandidateRecords(payload);
  if (records.length === 0) return undefined;

  const messageId =
    firstIdString(records, ["message_id", "messageId"]) ??
    optionalIdString(fallback.message_id);
  const sessionId =
    firstIdString(records, ["session_id", "sessionId"]) ??
    optionalIdString(fallback.session_id);
  const feedbackInput = firstOwnValue(records, [
    "current_user_feedback",
    "currentUserFeedback",
    "current_feedback",
    "feedback",
  ]);
  const currentUserFeedback = feedbackInput.found
    ? normalizeAgentCurrentUserFeedback(feedbackInput.value, {
        message_id: messageId,
        session_id: sessionId,
      })
    : undefined;

  if (!messageId && !sessionId && !feedbackInput.found) return undefined;

  return {
    ...(messageId ? { message_id: messageId } : {}),
    ...(sessionId ? { session_id: sessionId } : {}),
    ...(feedbackInput.found
      ? { current_user_feedback: currentUserFeedback ?? null }
      : {}),
  };
};

export const normalizeAgentConversationHistoryAssistantMetadata = (
  message: AgentConversationHistoryMessage,
  sessionId: AgentServiceId,
): NormalizedAgentAssistantMessageMetadata | undefined => {
  if (message.role !== "assistant") return undefined;

  return normalizeAgentAssistantMessageMetadata(message, {
    message_id: message.message_id,
    session_id: message.session_id ?? sessionId,
  });
};

export const agentAssistantMessageMetadataToCustom = (
  metadata: AgentAssistantMessageMetadata,
): Record<string, unknown> => {
  const custom: Record<string, unknown> = {};
  const messageId = optionalIdString(metadata.message_id);
  const sessionId = optionalIdString(metadata.session_id);
  if (messageId) custom.message_id = messageId;
  if (sessionId) custom.session_id = sessionId;
  if (hasOwn(metadata as Record<string, unknown>, "current_user_feedback")) {
    custom.current_user_feedback =
      normalizeAgentCurrentUserFeedback(metadata.current_user_feedback, {
        message_id: messageId,
        session_id: sessionId,
      }) ?? null;
  }
  return custom;
};

const normalizeAgentMessageFeedbackResponse = (
  feedback: AgentMessageFeedbackResponse,
): AgentMessageFeedbackResponse => ({
  ...feedback,
  id: stringifyAgentServiceId(feedback.id) ?? feedback.id,
  message_id:
    stringifyAgentServiceId(feedback.message_id) ?? feedback.message_id,
  session_id:
    stringifyAgentServiceId(feedback.session_id) ?? feedback.session_id,
});

/** Per-interrupt metadata on `pending_interrupt` SSE — index-aligned with payload. */
export interface InterruptMeta {
  interrupt_id: string;
}

/** Body of a 409 response when `resolves_interrupt_id` is stale. */
export interface AlreadyResolvedBody {
  state: "not_pending" | string;
}

export interface DiffSegment {
  op: "equal" | "delete" | "insert";
  text: string;
}

/** A block of the backend's block-level summary diff (render_summary_block_diff). */
export type DiffBlock =
  | { op: "equal" | "insert" | "delete"; markdown: string }
  | { op: "replace"; prefix: string; segments: DiffSegment[] };

export interface FieldChange {
  from?: unknown;
  to?: unknown;
  /** Server-sent display name for the field. */
  label?: string;
  /** Block-structured diff for the rich preview. */
  diff_blocks?: DiffBlock[];
  /** Flat word-level diff. Fallback for older backends that predate diff_blocks. */
  diff_segments?: DiffSegment[];
}

export interface InterruptPayload {
  action: string;
  /** Server-sent display heading for the action. */
  action_label?: string;
  /** Server-resolved name of what is being changed. The wire names it under a
   *  different key per action (task_title, prep_title, client_label, …); the
   *  server resolves it so no client keeps a copy of that list. */
  target_title?: string;
  changes?: Record<string, FieldChange>;
  [key: string]: unknown;
}

export interface AgentChatCallbacks {
  onToken?: (token: string) => void;
  onDone?: (response: AgentChatResponse) => void;
  onError?: (error: string) => void;
  /** Backend emitted a `pending_interrupt`. `meta` is undefined on old deploys. */
  onInterrupt?: (
    payloads: InterruptPayload[],
    confirmationCue?: string,
    meta?: InterruptMeta[],
  ) => void;
  /** Chat POST returned 409 — the targeted interrupt is no longer pending. */
  onAlreadyResolved?: (body: AlreadyResolvedBody) => void;
}

const isInterruptPayload = (value: unknown): value is InterruptPayload => {
  if (!value || typeof value !== "object") return false;
  return typeof (value as { action?: unknown }).action === "string";
};

export const isInterruptMeta = (value: unknown): value is InterruptMeta => {
  if (!value || typeof value !== "object") return false;
  const id = (value as { interrupt_id?: unknown }).interrupt_id;
  return typeof id === "string" && id.length > 0;
};

const AGENT_SERVICE_CHAT = "agent-service-chat";
const AGENT_SERVICE_FEEDBACK_PATH = "/api/agent-proxy/api/v1/chat/messages";
const AGENT_SERVICE_SESSION_PATH = "/api/agent-proxy/api/v1/session";

interface AgentServiceErrorBody {
  subcode?: unknown;
  statusCode?: unknown;
  message?: unknown;
  error?: unknown;
  detail?: unknown;
  [key: string]: unknown;
}

const parseJsonRecord = (value: string): AgentServiceErrorBody | undefined => {
  try {
    const parsed = JSON.parse(value);
    return isRecord(parsed) ? (parsed as AgentServiceErrorBody) : undefined;
  } catch {
    return undefined;
  }
};

const parseStructuredErrorValue = (
  value: unknown,
): AgentServiceErrorBody | undefined => {
  if (typeof value === "string") return parseJsonRecord(value);
  return isRecord(value) ? (value as AgentServiceErrorBody) : undefined;
};

const parseAgentServiceErrorBody = (
  responseText: string,
): AgentServiceErrorBody | undefined => {
  const body = responseText ? parseJsonRecord(responseText) : undefined;
  if (!body) return undefined;

  const structuredError = parseStructuredErrorValue(body.error);
  if (structuredError) {
    const { error: _wrappedError, ...rest } = body;
    return { ...rest, ...structuredError };
  }

  const structuredDetail = parseStructuredErrorValue(body.detail);
  return structuredDetail ? { ...body, ...structuredDetail } : body;
};

const agentMessageFeedbackUrl = (messageId: string) =>
  `${AGENT_SERVICE_FEEDBACK_PATH}/${encodeURIComponent(messageId)}/feedback`;

const agentSessionHistoryUrl = (sessionId: string, limit?: number) => {
  const query =
    typeof limit === "number"
      ? `?limit=${encodeURIComponent(String(limit))}`
      : "";
  return `${AGENT_SERVICE_SESSION_PATH}/${encodeURIComponent(sessionId)}/history${query}`;
};

const readAgentServiceError = async (
  response: Response,
): Promise<TServerErrorResponse> => {
  const responseText = await response.text().catch(() => "");
  const body = parseAgentServiceErrorBody(responseText);
  const data: Record<string, unknown> = {
    ...(body ?? {}),
    subcode: isValidSubcode(body?.subcode)
      ? body.subcode
      : ErrorSubcode.UNKNOWN_ERROR,
    statusCode:
      typeof body?.statusCode === "number" ? body.statusCode : response.status,
  };

  if (!body && responseText) {
    data.error = responseText;
  }

  return {
    response: {
      status: response.status,
      data,
    },
  } as unknown as TServerErrorResponse;
};

/**
 * Stream a chat message to the agent service via SSE (/api/v1/agent/chat).
 * Tokens are emitted incrementally via onToken; the final response via onDone.
 */
export const agentServiceChat = async (
  data: AgentChatRequest,
  callbacks: AgentChatCallbacks,
): Promise<AgentChatResponse> => {
  const response = await fetch("/api/agent-proxy/api/v1/agent/chat", {
    method: "POST",
    headers: {
      Accept: "text/event-stream",
      "Content-Type": "application/json",
    },
    cache: "no-store",
    body: JSON.stringify(data),
  });

  // 409 → stale `resolves_interrupt_id`. FastAPI wraps as { detail: {...} }.
  if (response.status === 409) {
    const raw = (await response.json().catch(() => null)) as {
      detail?: AlreadyResolvedBody;
    } | null;
    callbacks.onAlreadyResolved?.(raw?.detail ?? { state: "not_pending" });
    return { session_id: data.session_id ?? "", response: "" };
  }

  if (!response.ok || !response.body) {
    const errorText = await response.text().catch(() => "Unknown error");
    callbacks.onError?.(errorText);
    throw new Error(errorText);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let sessionId = "";
  let fullResponse = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Parse SSE events from the buffer
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    let eventType = "";
    for (const line of lines) {
      if (line.startsWith("event: ")) {
        eventType = line.slice(7).trim();
      } else if (line.startsWith("data: ")) {
        const jsonStr = line.slice(6);
        let event;
        try {
          event = JSON.parse(jsonStr);
        } catch {
          // Skip malformed JSON lines
          continue;
        }

        sessionId = stringifyAgentServiceId(event.session_id) ?? sessionId;

        switch (eventType || event.event) {
          case "token":
            callbacks.onToken?.(event.content);
            fullResponse += event.content;
            break;
          case "pending_interrupt": {
            const raw = event.payload;
            if (raw == null) break;
            const payloads = (Array.isArray(raw) ? raw : [raw]).filter(
              isInterruptPayload,
            );
            if (payloads.length > 0) {
              const cue =
                typeof event.confirmation_cue === "string"
                  ? event.confirmation_cue
                  : undefined;
              // Parse `interrupts` if present and 1:1 with payloads.
              const rawMeta = (event as { interrupts?: unknown }).interrupts;
              let meta: InterruptMeta[] | undefined;
              if (
                Array.isArray(rawMeta) &&
                rawMeta.length === payloads.length &&
                rawMeta.every(isInterruptMeta)
              ) {
                meta = rawMeta as InterruptMeta[];
              }
              callbacks.onInterrupt?.(payloads, cue, meta);
            }
            break;
          }
          case "done":
            fullResponse = event.response || fullResponse;
            break;
          case "error":
            callbacks.onError?.(event.error);
            reader.cancel();
            throw new Error(event.error || "Agent service error");
        }
      }
    }
  }

  const result: AgentChatResponse = {
    session_id: sessionId,
    response: fullResponse,
  };
  callbacks.onDone?.(result);
  return result;
};

export const upsertAgentMessageFeedback = async ({
  messageId,
  rating,
  reason,
}: UpsertAgentMessageFeedbackParams): Promise<AgentMessageFeedbackResponse> => {
  const body: AgentMessageFeedbackRequest = { rating, reason };
  const response = await fetch(agentMessageFeedbackUrl(messageId), {
    method: "PUT",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
    },
    cache: "no-store",
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    throw await readAgentServiceError(response);
  }

  return normalizeAgentMessageFeedbackResponse(
    (await response.json()) as AgentMessageFeedbackResponse,
  );
};

export const deleteAgentMessageFeedback = async (
  messageId: string,
): Promise<void> => {
  const response = await fetch(agentMessageFeedbackUrl(messageId), {
    method: "DELETE",
    headers: {
      Accept: "application/json",
    },
    cache: "no-store",
  });

  if (response.status === 204 || response.ok) return;

  throw await readAgentServiceError(response);
};

export const getAgentSessionHistory = async (
  sessionId: string,
  options: { limit?: number } = {},
): Promise<AgentConversationHistoryResponse> => {
  const response = await fetch(
    agentSessionHistoryUrl(sessionId, options.limit),
    {
      method: "GET",
      headers: {
        Accept: "application/json",
      },
      cache: "no-store",
    },
  );

  if (!response.ok) {
    throw await readAgentServiceError(response);
  }

  const body = (await response.json()) as AgentConversationHistoryResponse;
  return {
    ...body,
    session_id: stringifyAgentServiceId(body.session_id) ?? sessionId,
  };
};
